import React, { useState, useEffect } from "react";
import Modal from "./Modal";
import PrimaryButton from "../components/Buttons/PrimaryButton";
import SecondaryButton from "../components/Buttons/SecondaryButton";

interface AddReactionModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  pageNumber: number;
  onSubmit: (rxnSmiles: string) => Promise<void>;
  mode?: "add" | "edit";
  initialRxnSmiles?: string;
}

const AddReactionModal: React.FC<AddReactionModalProps> = ({
  isOpen,
  setIsOpen,
  pageNumber,
  onSubmit,
  mode = "add",
  initialRxnSmiles = "",
}) => {
  const [rxnSmiles, setRxnSmiles] = useState(initialRxnSmiles);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setRxnSmiles(initialRxnSmiles);
      setError(null);
      setIsSubmitting(false);
    }
  }, [isOpen, initialRxnSmiles]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = rxnSmiles.trim();
    if (!trimmed) {
      setError("Reaction SMILES is required");
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      await onSubmit(trimmed);
      setIsOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || "Failed to add reaction");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} setIsOpen={setIsOpen}>
      <div className="w-full">
        <h3 className="text-base font-semibold tracking-tight text-gray-900 mb-0.5">
          {mode === "edit" ? "Edit reaction" : "Add reaction"} on page {pageNumber}
        </h3>
        <p className="text-sm text-gray-500 mb-5">
          {mode === "edit"
            ? "Update the reaction SMILES for this manually-added reaction."
            : "Manually attach a reaction SMILES for a reaction that wasn't detected."}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="add-reaction-smiles" className="block text-sm font-medium text-gray-700 mb-1.5">
              Reaction SMILES
            </label>
            <input
              id="add-reaction-smiles"
              type="text"
              value={rxnSmiles}
              onChange={(e) => setRxnSmiles(e.target.value)}
              placeholder="e.g. CCO.O>>CC=O"
              className="w-full h-10 px-3 text-sm font-mono border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1d365d] focus:border-[#1d365d] transition-colors disabled:bg-gray-50"
              autoFocus
              disabled={isSubmitting}
              spellCheck={false}
            />
            <p className="mt-1.5 text-xs text-gray-400">
              Use <span className="font-mono">{">>"}</span> to separate reactants from products (e.g.{" "}
              <span className="font-mono">A.B{">>"}</span><span className="font-mono">C</span>).
            </p>
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </p>
          )}

          <div className="flex gap-2 pt-2">
            <SecondaryButton
              type="button"
              onClick={() => setIsOpen(false)}
              disabled={isSubmitting}
              className="flex-1"
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              type="submit"
              disabled={isSubmitting || !rxnSmiles.trim()}
              className="flex-1"
            >
              {isSubmitting
                ? mode === "edit"
                  ? "Saving..."
                  : "Adding..."
                : mode === "edit"
                  ? "Save changes"
                  : "Add reaction"}
            </PrimaryButton>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default AddReactionModal;
